import { Equal, Hash } from 'effect';

import type { PageRequest } from '@vs-shop/domain/pagination';

import { facetKeyId } from './facetKey';
import { canonicalSelection, type ListingSelection } from './listingSelection';
import type { ListingScope } from './listingScope';
import { scopeKeyOf } from './listingScope';

export type ListingRequest = {
  readonly scope: ListingScope;
  readonly selection: ListingSelection;
};

export type ListingPageRequest = ListingRequest & PageRequest;

/**
 * The filters of a canonical selection as nested arrays.
 *
 * Arrays keep their order through `JSON.stringify` and objects do not, so two
 * equal selections built in different ways always give the same key.
 */
const filterParts = (selection: ListingSelection) => [
  selection.terms.map((term) => [facetKeyId(term.key), term.values]),
  selection.ranges.map((range) => [facetKeyId(range.key), range.min, range.max]),
];

/** Returns the stable page cache key for a canonical listing request. */
export const listingRequestKey = (request: ListingPageRequest): string => {
  const selection = canonicalSelection(request.selection);

  return JSON.stringify([
    scopeKeyOf(request.scope),
    ...filterParts(selection),
    [selection.sort.field, selection.sort.direction],
    request.page,
    request.itemsPerPage,
  ]);
};

/** Returns the stable facet cache key without page or sort state. */
export const facetsRequestKey = (request: ListingRequest): string =>
  JSON.stringify([
    scopeKeyOf(request.scope),
    ...filterParts(canonicalSelection(request.selection)),
  ]);

/**
 * One listing request as a Value Object, equal to another when their request keys
 * are equal.
 *
 * The id is `listingRequestKey` or `facetsRequestKey`. The request is only the
 * lookup input and takes no part in equality. An atom family and a cache that key
 * by this class therefore agree on what "the same request" means.
 */
export class ListingCacheKey<Request> implements Equal.Equal {
  /** The key of a products page request. */
  static products(request: ListingPageRequest): ListingCacheKey<ListingPageRequest> {
    return new ListingCacheKey(listingRequestKey(request), request);
  }

  /** The key of a facets request. It has no page and no sort. */
  static facets(request: ListingRequest): ListingCacheKey<ListingRequest> {
    return new ListingCacheKey(facetsRequestKey(request), request);
  }

  private constructor(
    readonly id: string,
    readonly request: Request
  ) {}

  [Equal.symbol](that: Equal.Equal): boolean {
    return that instanceof ListingCacheKey && that.id === this.id;
  }

  [Hash.symbol](): number {
    return Hash.string(this.id);
  }
}
